import { useCallback, useLayoutEffect, useRef } from 'react';

import { type FetchSearchResultsForRangeType } from '~/providers/TranscriptSearchResultsProvider';
import { MAX_TRANSCRIPT_RANGE_IN_MS } from '~/swr-hooks/clips/transcript/constants';
import { MIN_TRANSCRIPT_QUERY_LENGTH } from '~/swr-hooks/clips/transcript/search/useSearchPrivateTranscript';

/**
 * This hook provides logic for fetching search results for the transcript search.
 */
export const useFetchSearchResultsLogic = () => {
  const isMounted = useRef(false);
  const currentSearchTextRef = useRef('');

  useLayoutEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const canContinueFetching = useCallback(
    (searchText: string) => isMounted.current && searchText === currentSearchTextRef.current,
    [],
  );

  /**
   * Search results loading:
   * 1. Fetch results for time [0, MAX_TRANSCRIPT_RANGE_IN_MS] - this will provide first and next results to go to
   *    1a. If there is no results for the first range, fetch results for the next range (nextStartTime + MAX_TRANSCRIPT_RANGE_IN_MS)
   * 2. Fetch results for time [videoTime - MAX_TRANSCRIPT_RANGE_IN_MS, videoTime] - this will provide previous results
   *    2a. If there is no results for the last range, fetch results for the previous range (prevEndTime - MAX_TRANSCRIPT_RANGE_IN_MS)
   * 3. Fetch results for current time
   */
  const fetchInitialSearchResults = useCallback(
    async ({
      searchText,
      fetchSearchResultsForRange,
      clipDurationInMs,
      videoTime,
    }: {
      videoTime: number;
      searchText: string;
      clipDurationInMs: number;
      fetchSearchResultsForRange: FetchSearchResultsForRangeType;
    }) => {
      if (searchText && searchText.length >= MIN_TRANSCRIPT_QUERY_LENGTH) {
        currentSearchTextRef.current = searchText;
        let results = await fetchSearchResultsForRange({ startTimeMs: 0, endTimeMs: MAX_TRANSCRIPT_RANGE_IN_MS });

        if (!results) return;
        if (!canContinueFetching(searchText)) return;
        // if the first fetch returns no results, try to make another call with given nextStartTime to ensure we have some results
        if (results.data.length === 1 && results.hasMore && results.data[0].data.length === 0) {
          const nextResultsTime = results.data[0].endTimeMs;
          results = await fetchSearchResultsForRange({
            startTimeMs: nextResultsTime,
            endTimeMs: nextResultsTime + MAX_TRANSCRIPT_RANGE_IN_MS,
          });
        }
        if (!canContinueFetching(searchText)) return;

        if (results?.hasMore) {
          // if there is more results to fetch, fetch the last page of results for the end of the video
          const lastResults = await fetchSearchResultsForRange({
            startTimeMs: clipDurationInMs - MAX_TRANSCRIPT_RANGE_IN_MS,
            endTimeMs: clipDurationInMs,
          });

          if (!canContinueFetching(searchText) || !lastResults) return;

          const lastResult = lastResults.data[lastResults.data.length - 1];
          if (lastResult.data.length === 0) {
            // if there is no results at the end of the video, fetch previous range that has results
            await fetchSearchResultsForRange({
              startTimeMs: lastResult.startTimeMs - MAX_TRANSCRIPT_RANGE_IN_MS,
              endTimeMs: lastResult.startTimeMs,
            });
          }

          if (!canContinueFetching(searchText)) return;

          if (videoTime > 0) {
            // also, fetch results for current time
            const timeInMs = videoTime * 1000;
            const startTimeMs = Math.max(0, timeInMs - MAX_TRANSCRIPT_RANGE_IN_MS / 2);
            const endTimeMs =
              startTimeMs === 0 ? MAX_TRANSCRIPT_RANGE_IN_MS : timeInMs + MAX_TRANSCRIPT_RANGE_IN_MS / 2;

            await fetchSearchResultsForRange({ startTimeMs, endTimeMs });
          }
        }
      }
    },
    [canContinueFetching],
  );

  return {
    fetchInitialSearchResults,
  };
};
